
import { defineComponent, onMounted, provide, ref, watch, watchEffect } from 'vue'
import { throttle } from 'lodash'
import { storeToRefs } from 'pinia'
import { useMainStore, useSlidesStore, useKeyboardStore } from '@/store'
import { ContextmenuItem } from '@/components/Contextmenu/types'
import { ClickmenuItemArray } from '@/views/Editor/FloatingMenu/types'
import { PPTElement } from '@/types/slides'
import { AlignmentLineProps } from '@/types/edit'
import { injectKeySlideScale } from '@/types/injectKey'
import { removeAllRanges } from '@/utils/selection'
import { KEYS } from '@/configs/hotkey'

import useViewportSize from './hooks/useViewportSize'
import useMouseSelection from './hooks/useMouseSelection'
import useDropImageOrText from './hooks/useDropImageOrText'
import useRotateElement from './hooks/useRotateElement'
import useScaleElement from './hooks/useScaleElement'
import useSelectElement from './hooks/useSelectElement'
import useDragElement from './hooks/useDragElement'
import useDragLineElement from './hooks/useDragLineElement'
import useInsertFromCreateSelection from './hooks/useInsertFromCreateSelection'

import useDeleteElement from '@/hooks/useDeleteElement'
import useCopyAndPasteElement from '@/hooks/useCopyAndPasteElement'
import useSelectAllElement from '@/hooks/useSelectAllElement'
import useScaleCanvas from '@/hooks/useScaleCanvas'
import useScreening from '@/hooks/useScreening'
import useSlideHandler from '@/hooks/useSlideHandler'

import useHistorySnapshot from '@/hooks/useHistorySnapshot'

import EditableElement from './EditableElement.vue'
import MouseSelection from './MouseSelection.vue'
import ViewportBackground from './ViewportBackground.vue'
import AlignmentLine from './AlignmentLine.vue'
import Ruler from './Ruler.vue'
import ElementCreateSelection from './ElementCreateSelection.vue'
import MultiSelectOperate from './Operate/MultiSelectOperate.vue'
import Operate from './Operate/index.vue'
import LinkDialog from './LinkDialog.vue'
import {
  Copy,
  UpC,
  DownC,
  Delete,
  AddItem,
  Tag
} from '@icon-park/vue-next'
import {
  Tooltip,
} from 'ant-design-vue'

export default defineComponent({
  name: 'editor-canvas',
  components: {
    EditableElement,
    MouseSelection,
    ViewportBackground,
    AlignmentLine,
    Ruler,
    ElementCreateSelection,
    MultiSelectOperate,
    Operate,
    LinkDialog,
    Copy,
    UpC,
    DownC,
    Delete,
    AddItem,
    Tag,
    Tooltip
  },
  setup() {
    const mainStore = useMainStore()
    const {
      activeElementIdList,
      activeGroupElementId,
      handleElementId,
      editorAreaFocus,
      showGridLines,
      showRuler,
      creatingElement,
      canvasScale,
    } = storeToRefs(mainStore)
    const { currentSlide, slideIndex, slides } = storeToRefs(useSlidesStore())
    const { ctrlKeyState, spaceKeyState } = storeToRefs(useKeyboardStore())

    const { addHistorySnapshot } = useHistorySnapshot()

    const viewportRef = ref<HTMLElement>()
    const alignmentLines = ref<AlignmentLineProps[]>([])

    const linkDialogVisible = ref(false)
    const openLinkDialog = () => linkDialogVisible.value = true

    watch(handleElementId, () => {
      mainStore.setActiveGroupElementId('')
    })

    const elementList = ref<PPTElement[]>([])
    const setLocalElementList = () => {
      elementList.value = currentSlide.value ? JSON.parse(JSON.stringify(currentSlide.value.elements)) : []
    }
    watchEffect(setLocalElementList)

    const canvasRef = ref<HTMLElement>()
    const { dragViewport, viewportStyles } = useViewportSize(canvasRef)

    useDropImageOrText(canvasRef)

    const { mouseSelection, mouseSelectionVisible, mouseSelectionQuadrant, updateMouseSelection } = useMouseSelection(elementList, viewportRef)

    const { dragElement } = useDragElement(elementList, alignmentLines)
    const { dragLineElement } = useDragLineElement(elementList)
    const { selectElement } = useSelectElement(elementList, dragElement)
    const { scaleElement, scaleMultiElement } = useScaleElement(elementList, alignmentLines)
    const { rotateElement } = useRotateElement(elementList, viewportRef)

    const { selectAllElement } = useSelectAllElement()
    const { deleteAllElements } = useDeleteElement()
    const { pasteElement } = useCopyAndPasteElement()
    const { enterScreeningFromStart } = useScreening()
    const { createSlide, copyAndPasteSlide, deleteSlide, sortSlides, updateSlideIndex } = useSlideHandler()

    // 组件渲染时，如果存在元素焦点，需要清除
    // 这种情况存在于：有焦点元素的情况下进入了放映模式，再退出时，需要清除原先的焦点（因为可能已经切换了页面）
    onMounted(() => {
      if (activeElementIdList.value.length) mainStore.setActiveElementIdList([])
    })

    // 点击画布的空白区域：清空焦点元素、设置画布焦点、清除文字选区
    const handleClickBlankArea = (e: MouseEvent) => {
      mainStore.setActiveElementIdList([])

      if (!spaceKeyState.value) updateMouseSelection(e)
      else dragViewport(e)

      if (!editorAreaFocus.value) mainStore.setEditorareaFocus(true)
      removeAllRanges()
    }

    // 移除画布编辑区域焦点
    const removeEditorAreaFocus = () => {
      if (editorAreaFocus.value) mainStore.setEditorareaFocus(false)
    }

    // 滚动鼠标
    const { scaleCanvas, resetCanvas, setCanvasScalePercentage, canvasScalePercentage } = useScaleCanvas()
    const throttleScaleCanvas = throttle(scaleCanvas, 100, { leading: true, trailing: false })
    const throttleUpdateSlideIndex = throttle(updateSlideIndex, 300, { leading: true, trailing: false })

    const canvasScalePresetList = [200, 150, 100, 80, 50]
    const canvasScaleVisible = ref(false)

    const applyCanvasPresetScale = (value: number) => {
      setCanvasScalePercentage(value)
      canvasScaleVisible.value = false
    }

    const handleMousewheelCanvas = (e: WheelEvent) => {
      e.preventDefault()

      // 按住Ctrl键时：缩放画布
      if (ctrlKeyState.value) {
        if (e.deltaY > 0) throttleScaleCanvas('-')
        else if (e.deltaY < 0) throttleScaleCanvas('+')
      }
      // 上下翻页
      else {
        if (e.deltaY > 0) throttleUpdateSlideIndex(KEYS.DOWN)
        else if (e.deltaY < 0) throttleUpdateSlideIndex(KEYS.UP)
      }
    }

    // 开关网格线
    const toggleGridLines = () => {
      mainStore.setGridLinesState(!showGridLines.value)
    }

    // 开关标尺
    const toggleRuler = () => {
      mainStore.setRulerState(!showRuler.value)
    }

    // 在鼠标绘制的范围插入元素
    const { insertElementFromCreateSelection } = useInsertFromCreateSelection(viewportRef)

    const contextmenus = (): ContextmenuItem[] => {
      return [
        {
          text: '粘贴',
          subText: 'Ctrl + V',
          handler: pasteElement,
        },
        {
          text: '全选',
          subText: 'Ctrl + A',
          handler: selectAllElement,
        },
        {
          text: '网格线',
          subText: showGridLines.value ? '√' : '',
          handler: toggleGridLines,
        },
        {
          text: '标尺',
          subText: showRuler.value ? '√' : '',
          handler: toggleRuler,
        },
        {
          text: '重置当前页',
          handler: deleteAllElements,
        },
        { divider: true },
        {
          text: '幻灯片放映',
          subText: 'F5',
          handler: enterScreeningFromStart,
        },
      ]
    }

    const operateMenus = (): ClickmenuItemArray => {
      return {
        hide: !activeElementIdList.value.length,
      }
    }

    const openRemark = () => {
      mainStore.setRemarkState(!mainStore.showRemark)
    }

    // ppt上移下移
    const pageChange = (type: number) => {
      if (type > 0) {
        if (slideIndex.value === slides.value.length - 1) return
        sortSlides(slideIndex.value + type, slideIndex.value)
      }
      else {
        if (slideIndex.value === 0) return
        sortSlides(slideIndex.value + type, slideIndex.value)
      }
      // 添加历史快照
      addHistorySnapshot()
    }

    provide(injectKeySlideScale, canvasScale)

    return {
      elementList,
      activeElementIdList,
      handleElementId,
      activeGroupElementId,
      canvasRef,
      viewportRef,
      viewportStyles,
      canvasScale,
      mouseSelection,
      mouseSelectionVisible,
      mouseSelectionQuadrant,
      creatingElement,
      alignmentLines,
      linkDialogVisible,
      spaceKeyState,
      showRuler,
      openLinkDialog,
      handleClickBlankArea,
      removeEditorAreaFocus,
      insertElementFromCreateSelection,
      selectElement,
      rotateElement,
      scaleElement,
      dragLineElement,
      scaleMultiElement,
      handleMousewheelCanvas,
      contextmenus,
      operateMenus,
      openRemark,
      pageChange,
      createSlide, 
      copyAndPasteSlide, 
      deleteSlide,
      resetCanvas,
      scaleCanvas,
      applyCanvasPresetScale,
      canvasScalePresetList,
      canvasScalePercentage,
      canvasScaleVisible,
    }
  },
})
